import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Button, Form, Container, Spinner } from "react-bootstrap";
import { API, graphqlOperation } from "aws-amplify";
import moment from "moment";

// Components
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination.js";
import CustomReactDatetime from "components/Custom/CustomReactDatetime.js";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import NotificationAlert from "react-notification-alert";

// Context
import { AuthContext } from "contexts/authContext";
import { useTour } from "@reactour/tour";
import { messageStorageExternalSteps } from "./MessageStorageSteps";

// Utilities
import { convertBytesToGB } from "utilities/commonUtilities";

// GraphQL queries
import {
  getChannelMessageStorage,
  getClientEnvironmentMessageStorage,
  getClientMessageStorage
} from "graphql/queries.js";

function MessageStorage() {
  const notify = useContext(NotificationAlertContext);
  const { clientId, clientEnvironmentId, channelId } = useParams();
  const location = useLocation();
  const { user, groupName } = useContext(AuthContext);
  const isInternalUser = groupName === "internal" || groupName === "internal_readonly";

  // Tour Guide
  const { setIsOpen, setCurrentStep, setSteps } = useTour();

  // Get all query parameters and state at once
  const getQueryParameters = () => {
    const queryParams = new URLSearchParams(location.search);
    const selectedDateParam = queryParams.get('selectedDate');
    const compareDateParam = queryParams.get('compareDate');
    // Check query parameters only
    return {
      selectedDate: selectedDateParam && selectedDateParam !== '' ? moment(selectedDateParam) : 
                   moment().subtract(1, "day"),
      compareDate: compareDateParam && compareDateParam !== '' ? moment(compareDateParam) : 
                  null,
      clientName: queryParams.get('clientName') || '',
      environmentName: queryParams.get('environmentName') || '',
      channelName: queryParams.get('channelName') || '',
      allotmentGb: queryParams.get('allotmentGb')
    };
  };

  // Get query parameters once
  const queryParams = getQueryParameters();

  // State management
  const [selectedDate, setSelectedDate] = useState(queryParams.selectedDate);
  const [compareDate, setCompareDate] = useState(queryParams.compareDate);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [pageTitle, setPageTitle] = useState("Message Storage");
  const [reportType, setReportType] = useState("");
  const [totalStorageGb, setTotalStorageGb] = useState(0);
  const [totalPreviousStorageGb, setTotalPreviousStorageGb] = useState(0);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelDetailData, setChannelDetailData] = useState([]);
  const [channelDetailTotalCount, setChannelDetailTotalCount] = useState(0);
  const [showChannelDetail, setShowChannelDetail] = useState(false);
  const [clientEnvDetails, setClientEnvDetails] = useState({});
  const [entitlementInfo, setEntitlementInfo] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasDate1Data, setHasDate1Data] = useState(true);
  const [hasDate2Data, setHasDate2Data] = useState(true);

  // Ref to prevent duplicate API calls
  const fetchInProgress = useRef(false);

  // Dynamic CSV Headers function
  const getDynamicCSVHeaders = () => {
    if (reportType === "client") {
      return [
        { label: "Environment ID", key: "environment_id" },
        { label: "Environment Name", key: "environment_name" },
        { label: `Storage On ${selectedDate.format('YYYY-MM-DD')} (GB)`, key: "current_storage_gb" },
        ...(compareDate ? [{ label: `Storage On ${compareDate.format('YYYY-MM-DD')} (GB)`, key: "previous_storage_gb" }] : [])
      ];
    } else if (reportType === "environment") {
      return [
        { label: "Channel ID", key: "channel_id" },
        { label: "Channel Name", key: "channel_name" },
        { label: `Storage On ${selectedDate.format('YYYY-MM-DD')} (GB)`, key: "s3_usage_bytes_date_1" },
        ...(compareDate ? [{ label: `Storage On ${compareDate.format('YYYY-MM-DD')} (GB)`, key: "s3_usage_bytes_date_2" }] : [])
      ];
    } else if (reportType === "channel") {
      return [
        { label: "Date", key: "s3_archive_date1" },
        { label: "Storage (GB)", key: "s3_usage_bytes_date_1" }
      ];
    }
    return columns.map(col => ({ label: col.Header, key: col.accessor }));
  };

  // Pagination Starts
  const getColumnMapping = () => {
    const mapping = {
      "Environment": "ENVIRONMENT_NAME",
      "Channel": "CHANNEL_NAME",
      "Date": "S3_ARCHIVE_DATE1",
      "Storage (GB)": "S3_USAGE_BYTES_DATE_1",
      "current_storage_gb": "S3_USAGE_BYTES_DATE_1",
      "previous_storage_gb": "S3_USAGE_BYTES_DATE_2",
    };

    // Add dynamic date-based column mappings
    if (selectedDate) {
      mapping[`Storage On ${selectedDate.format('YYYY-MM-DD')} (GB)`] = "S3_USAGE_BYTES_DATE_1";
    }
    if (compareDate) {
      mapping[`Storage On ${compareDate.format('YYYY-MM-DD')} (GB)`] = "S3_USAGE_BYTES_DATE_2";
    }

    return mapping;
  };
  const numberOfRowsData = [25, 50, 100, 1000];

  const [currentLimit, setCurrentLimit] = useState(numberOfRowsData[1]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [ascOrDescValue, setAscOrDescValue] = useState("ASC");
  const [sortValue, setSortValue] = useState(""); // Will be set based on reportType
  const [showDownloadGridResults, setShowDownloadGridResults] = useState(true);

  // Function to get initial sort value based on report type
  const getInitialSortValue = (type) => {
    switch(type) {
      case "client":
        return "Environment"; // Client report shows environments
      case "environment":
        return "Channel"; // Environment report shows channels
      case "channel":
        return "Date"; // Channel report shows dates
      default:
        return "Date";
    }
  };

  const setStateForPagnination = async (key, value) => {
    if (key === "currentLimit") {
      setCurrentLimit(value);
    } else if (key === "offset") {
      setCurrentOffset(value);
    } else if (key === "ascOrDesc") {
      setAscOrDescValue(value);
    } else {
      setSortValue(value);
    }
  };
  // Pagination Ends

  // Notification ref
  const notificationAlertRef = React.useRef(null);

  // Determine report context from route
  useEffect(() => {
    const path = location.pathname;

    if (channelId && path.includes('channel-message-storage')) {
      setReportType("channel");
      setPageTitle("Channel Message Storage");
      setShowChannelDetail(true);
      setSortValue(getInitialSortValue("channel")); // Set sort value for channel
    } else if (path.includes('environment-message-storage') && clientEnvironmentId) {
      setReportType("environment");
      setPageTitle("Environment Message Storage");
      setColumns(getEnvironmentColumns());
      setShowChannelDetail(false);
      setSortValue(getInitialSortValue("environment")); // Set sort value for environment
    } else {
      setReportType("client");
      setPageTitle("Client Message Storage");
      setColumns(getClientColumns());
      setShowChannelDetail(false);
      setSortValue(getInitialSortValue("client")); // Set sort value for client
    }
  }, [location.pathname, clientId, clientEnvironmentId, channelId]);

  // Update columns based on report type
  const updateColumns = useCallback((clientEnvDetails, entitlementInfo, date1HasData = true, date2HasData = true) => {
    setColumns(reportType === "environment" ? getEnvironmentColumns(clientEnvDetails, entitlementInfo, date1HasData, date2HasData) : getClientColumns(clientEnvDetails, entitlementInfo, date1HasData, date2HasData));
  }, [reportType, selectedDate, compareDate]);

  // Column definitions for client context (shows environments)
  const getClientColumns = (clientEnvDetails, entitlementInfo, date1HasData = true, date2HasData = true) => {
    const columns = [
      {
        Header: "Environment",
        accessor: "environment_name",
        sortable: true,
        filterable: false,
        Cell: ({ row }) => (
          <Link
            to={{
              pathname: `/admin/clients/${clientId}/message-storage/${row.original.environment_id}/environment-message-storage`,
              search: `?selectedDate=${selectedDate?.format('YYYY-MM-DD') || ''}&compareDate=${compareDate?.format('YYYY-MM-DD') || ''}&clientName=${encodeURIComponent(queryParams.clientName || clientEnvDetails.client_name || '')}&environmentName=${encodeURIComponent(queryParams.environmentName || row.original.environment_name || '')}&allotmentGb=${encodeURIComponent(queryParams.allotmentGb || entitlementInfo?.allotment_gb || 0)}`
            }}
          >
            {row.original.environment_name}
          </Link>
        ),
      },
    ];

    // Only add date1 column if it has data
    if (date1HasData) {
      columns.push({
        Header: `Storage On ${selectedDate ? selectedDate.format('YYYY-MM-DD') : 'Date'} (GB)`,
        accessor: "current_storage_gb",
        sortable: true,
        filterable: false,
        Cell: ({ value }) => `${(value)} GB`,
      });
    }

    // Add compare column only if compareDate is available AND has data
    if (compareDate && date2HasData) {
      columns.push({
        Header: `Storage On ${compareDate.format('YYYY-MM-DD')} (GB)`,
        accessor: "previous_storage_gb",
        sortable: true,
        filterable: false,
        Cell: ({ value }) => `${(value)} GB`,
      });
    }

    // Add dummy delete button column
    /* columns.push({
      Header: "Action",
      accessor: "action",
      sortable: false,
      filterable: false,
      Cell: ({ row }) => {
        const environment_name = row.original.environment_name;
        const environment_id = row.original.environment_id;
        return (
          <Link
            className="btn btn-outline btn-danger ml-4"
            to={{
              pathname: `/admin/delete-messages`,
              search: `?startDate=${selectedDate?.format("YYYY-MM-DD") || ""}&clientId=${encodeURIComponent(clientId || "")}&clientName=${encodeURIComponent(clientEnvDetails.client_name || queryParams.clientName || "")}&environmentId=${encodeURIComponent(environment_id || "")}&environmentName=${encodeURIComponent(environment_name || "")}`,
            }}
          >
            Delete Data
          </Link>
        );
      },
    }); */

    return columns;
  };

  const getEnvironmentColumns = (clientEnvDetails, entitlementInfo, date1HasData = true, date2HasData = true) => {
    const columns = [
      {
        Header: "Channel",
        accessor: "channel_name",
        sortable: true,
        filterable: false,
        Cell: ({ row }) => (
          <Link
            to={{
              pathname: `/admin/clients/${clientId}/message-storage/${clientEnvironmentId}/environment-message-storage/${row.original.channel_id}/channel-message-storage`,
              search: `?selectedDate=${selectedDate?.format('YYYY-MM-DD') || ''}&compareDate=${compareDate?.format('YYYY-MM-DD') || ''}&clientName=${encodeURIComponent(clientEnvDetails.client_name || queryParams.clientName || '')}&environmentName=${encodeURIComponent(queryParams.environmentName || clientEnvDetails.environment_name || '')}&channelName=${encodeURIComponent(queryParams.channelName || row.original.channel_name || '')}&allotmentGb=${encodeURIComponent(queryParams.allotmentGb || entitlementInfo?.allotment_gb || 0)}`
            }}
          >
            {row.original.channel_name}
          </Link>
        ),
      },
    ];

    // Only add date1 column if it has data
    if (date1HasData) {
      columns.push({
        Header: `Storage On ${selectedDate ? selectedDate.format('YYYY-MM-DD') : 'Date'} (GB)`,
        accessor: "current_storage_gb",
        sortable: true,
        filterable: false,
        Cell: ({ value }) => `${(value)} GB`,
      });
    }

    // Add compare column only if compareDate is available AND has data
    if (compareDate && date2HasData) {
      columns.push({
        Header: `Storage On ${compareDate.format('YYYY-MM-DD')} (GB)`,
        accessor: "previous_storage_gb",
        sortable: true,
        filterable: false,
        Cell: ({ value }) => `${(value)} GB`,
      });
    }

    // Add dummy delete button column for environments
    /* columns.push({
      Header: "Action",
      accessor: "action",
      sortable: false,
      filterable: false,
      Cell: ({ row }) => {
        const channel_name = row.original.channel_name;
        const channel_id = row.original.channel_id;
        return (
          <Link
            className="btn btn-outline btn-danger ml-4"
            to={{
              pathname: `/admin/delete-messages`,
              search: `?startDate=${selectedDate?.format("YYYY-MM-DD") || ""}&clientId=${encodeURIComponent(clientId || "")}&clientName=${encodeURIComponent(clientEnvDetails.client_name || queryParams.clientName || "")}&environmentId=${encodeURIComponent(clientEnvironmentId || "")}&environmentName=${encodeURIComponent(clientEnvDetails.environment_name || queryParams.environmentName || "")}&channelId=${encodeURIComponent(channel_id || "")}&channelName=${encodeURIComponent(channel_name || "")}`,
            }}
          >
            Delete Data
          </Link>
        );
      },
    }); */

    return columns;
  };

  // Get the appropriate API call based on report type
  const getApiCall = () => {
    switch (reportType) {
      case "client":
        return getClientMessageStorage;
      case "environment":
        return getClientEnvironmentMessageStorage;
      case "channel":
        return getChannelMessageStorage;
      default:
        return getClientMessageStorage; // fallback
    }
  };

  // Get the appropriate response key based on report type
  const getResponseKey = () => {
    switch (reportType) {
      case "client":
        return "getClientMessageStorage";
      case "environment":
        return "getClientEnvironmentMessageStorage";
      case "channel":
        return "getChannelMessageStorage";
      default:
        return "getClientMessageStorage"; // fallback
    }
  };

  // Fetch data based on date range and context
  const fetchReportData = async (variables = {}, isCSVDownload = false) => {
    if (!selectedDate) {
      notify("danger", "Please select a date");
      return;
    }

    setLoading(true);

    try {

      // Prepare parameters based on report type
      let params = {
        date1: selectedDate.format("YYYY_MM_DD"),
        date2: compareDate?.format("YYYY_MM_DD"),
      };

      // Add specific parameters based on report type
      if (reportType === "client") {
        params.client_id = clientId;
      } else if (reportType === "environment") {
        params.client_environment_id = clientEnvironmentId;
        params.client_id = clientId; // Required for authorization
      } else if (reportType === "channel") {
        params.channel_id = channelId;
        params.client_environment_id = clientEnvironmentId; // Required for authorization
        params.client_id = clientId; // Required for authorization
      }

      variables = { ...variables, ...params };
      // Use the appropriate API call
      const apiCall = getApiCall();
      const responseKey = getResponseKey();
      const response = await API.graphql(graphqlOperation(apiCall, variables));

      if (response.data?.[responseKey]?.success) {
        setShowDownloadGridResults(true);
        const responseBody = response.data[responseKey].body || {};
        let processedData = [];

        // Get total count from API response
        const totalRecords = responseBody.total_count || responseBody.data.length;

        // Process the raw data response
        const rawData = responseBody.data || [];
        const responseClientEnvDetails = responseBody.client_env_details || {};
        const responseTotals = responseBody.storage_summary || {};
        const responseEntitlementInfo = responseBody.entitlement_info || null;

        if (!isCSVDownload) {
          // Update client environment details state
          setClientEnvDetails(responseClientEnvDetails);

          // Set entitlement info from API response
          setEntitlementInfo(responseEntitlementInfo);

          // Set storage_summary from API response
          setTotalStorageGb(convertBytesToGB(responseTotals.total_current_gb));
          setTotalPreviousStorageGb(convertBytesToGB(responseTotals.total_previous_gb));

          // Check if dates have data - look at first row to determine
          const firstRow = rawData[0] || {};
          const date1HasData = firstRow.s3_archive_date1 !== null && firstRow.s3_archive_date1 !== undefined;
          const date2HasData = compareDate && firstRow.s3_archive_date2 !== null && firstRow.s3_archive_date2 !== undefined;
          
          setHasDate1Data(date1HasData);
          setHasDate2Data(date2HasData);

          // Show warning if selected date has no data
          if (!date1HasData) {
            notify("warning", `No data available for ${selectedDate.format('YYYY-MM-DD')}`);
          }
          
          // Show warning if compare date has no data
          if (compareDate && !date2HasData) {
            notify("warning", `No data available for compare date ${compareDate.format('YYYY-MM-DD')}`);
          }

          // Don't reset the compareDate state - preserve user's selection
          // The compareDate should only be changed when user explicitly selects a new date

          // Transform raw data based on report type
          if (reportType === "client") {
            // Group by environment for client view
            const environmentMap = {};

            rawData.forEach(row => {
              const envId = row.client_environment_id;
              if (!environmentMap[envId]) {
                environmentMap[envId] = {
                  environment_id: envId,
                  environment_name: row.environment_name || envId,
                  current_storage_gb: 0,
                  previous_storage_gb: 0,
                };
              }

              // Convert bytes to GB and sum up
              environmentMap[envId].current_storage_gb = convertBytesToGB(row.s3_usage_bytes_date_1);
              environmentMap[envId].previous_storage_gb = convertBytesToGB(row.s3_usage_bytes_date_2);
            });

            processedData = Object.values(environmentMap);

            processedData['filter'] = {
              "client_name": responseClientEnvDetails.client_name
            }

          } else if (reportType === "environment") {
            // Show channels for environment view
            processedData = rawData.map(row => {
              const currentGb = convertBytesToGB(row.s3_usage_bytes_date_1);
              const previousGb = convertBytesToGB(row.s3_usage_bytes_date_2);
              return {
                environment_id: row.client_environment_id,
                environment_name: row.environment_name,
                channel_id: row.channel_id,
                channel_name: row.channel_name || `Channel ${row.channel_id?.substring(0, 8) || 'Unknown'}`,
                current_storage_gb: currentGb,
                previous_storage_gb: row.s3_archive_date2 ? previousGb : null,
              };
            });
          }

          setData(processedData);
          setTotalCount(totalRecords);

          // Update columns after processing the API response - pass date availability
          updateColumns(responseClientEnvDetails, responseEntitlementInfo, date1HasData, date2HasData);

          if (!isCSVDownload) {
            notify("success", "Report data loaded successfully");
          }

          return processedData; // Return data for CSV download
        } else {
          const filter = {
            client_name: responseClientEnvDetails.client_name,
            environment_name: responseClientEnvDetails.environment_name
          };
          return { messageDataForCSV: rawData, filter };
        }
      } else {
        throw new Error(response.data?.[responseKey]?.message || "Failed to fetch data");
      }
    } catch (error) {
      notify("danger", error.message || "Failed to load report data");
      setData([]);
      setTotalCount(0);
      setTotalStorageGb(0);
      setTotalPreviousStorageGb(0);
      setEntitlementInfo(null);
      return [];
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  };

  // Fetch channel data when in channel view mode
  const fetchChannelData = async (variables = {}, isCSVDownload = false) => {
    if (!channelId) return;

    setLoading(true);

    try {
      const params = {
        channel_id: channelId,
        client_environment_id: clientEnvironmentId, // Required for authorization
        client_id: clientId, // Required for authorization
        date1: selectedDate.format("YYYY_MM_DD"),
      };
      variables = { ...variables, ...params };
      if (!variables['sortFilter']) {
        variables['sortFilter'] = "S3_ARCHIVE_DATE1";
        variables['sortType'] = "DESC"
      }
      const response = await API.graphql(graphqlOperation(getChannelMessageStorage, variables));

      if (response.data?.getChannelMessageStorage?.success) {
        setShowDownloadGridResults(true);
        const responseBody = response.data.getChannelMessageStorage.body || {};
        const rawData = responseBody.data || [];
        const responseEntitlementInfo = responseBody.entitlement_info || null;

        // Get total count from API response
        const totalCount = responseBody.total_count || rawData.length;

        // Set channel and environment details
        const responseClientEnvDetails = responseBody.client_env_details || {};
        setClientEnvDetails(responseClientEnvDetails);

        // Set entitlement info from API response
        setEntitlementInfo(responseEntitlementInfo);

        setSelectedChannel({
          channel_id: channelId,
          channel_name: rawData[0]?.channel_name || responseClientEnvDetails.channel_name || `Channel ${channelId}`
        });

        // Create detail data with date and storage information from API response
        const detailData = [];

        rawData.forEach(row => {
          if (row.s3_usage_bytes_date_1) {
            detailData.push({
              date: row.s3_archive_date1 ? row.s3_archive_date1.replace(/_/g, '-') : 'Unknown',
              storage_gb: convertBytesToGB(row.s3_usage_bytes_date_1)
            });
          }
        });

        if (!isCSVDownload) {
          setChannelDetailData(detailData);
          setChannelDetailTotalCount(totalCount);
          notify("success", "Channel details loaded");
        } else {
          const filter = {
            client_name: responseClientEnvDetails.client_name,
            environment_name: responseClientEnvDetails.environment_name,
            channel_name: responseClientEnvDetails.channel_name
          };
          return { messageDataForCSV: rawData, filter };
        }
      } else {
        throw new Error(response.data?.getChannelMessageStorage?.message || "Failed to fetch channel data");
      }
    } catch (error) {
      notify("danger", error.message || "Failed to load channel details");
      setChannelDetailData([]);
      setChannelDetailTotalCount(0);
      setEntitlementInfo(null);
    } finally {
      setLoading(false);
    }
  };

  // Channel detail columns
  const getChannelDetailColumns = () => [
    {
      Header: "Date",
      accessor: "date",
      sortable: true,
      filterable: false,
    },
    {
      Header: "Storage (GB)",
      accessor: "storage_gb",
      sortable: true,
      filterable: false,
      Cell: ({ value }) => `${value} GB`,
    },
    /* {
      Header: "Action",
      accessor: "action",
      sortable: false,
      filterable: false,
      Cell: ({ row }) => {
        const date = row.original.date;
        return (
          <Link
            className="btn btn-outline btn-danger ml-4"
            to={{
              pathname: `/admin/delete-messages`,
              search: `?startDate=${date || ""}&clientId=${encodeURIComponent(clientId || "")}&clientName=${encodeURIComponent(queryParams.clientName || "")}&environmentId=${encodeURIComponent(clientEnvironmentId || "")}&environmentName=${encodeURIComponent(queryParams.environmentName || "")}&channelId=${encodeURIComponent(channelId || "")}&channelName=${encodeURIComponent(queryParams.channelName || "")}`,
            }}
          >
            Delete Data
          </Link>
        );
      },
    }, */
  ];

  // Handle date changes
  const handleSelectedDateChange = (date) => setSelectedDate(moment(date));
  const handleCompareDateChange = (date) => setCompareDate(moment(date));

  // Main data fetching effect - handles initial load and context changes
  useEffect(() => {
    if (!reportType) return;

    if (reportType === "channel" && channelId) {
      // Channel view: fetch channel data
      fetchChannelData();
    } else if (reportType !== "channel" && clientId) {
      fetchReportData();
    }
  }, [reportType, clientId, clientEnvironmentId, channelId]);

  if (loading) return <Spinner animation="grow" />;

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>

      <Container fluid>
        <Row>
          <Col>
            <h2>{pageTitle}</h2>
          </Col>
          {groupName === "external" && (
            <Col className="my-auto text-right">
              <Button
                variant="secondary"
                className="btn-outline"
                onClick={() => {
                  setSteps(messageStorageExternalSteps);
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <i className="fas fa-desktop"></i> View Our Tour Guide
              </Button>
            </Col>
          )}
        </Row>

        <Row>
          <Col md="12" id="client-details-header">
            {/* Context Information Display */}
            <div className="mb-3">
              {reportType === "client" && (
                <div>
                  <h5>Client Name: {clientEnvDetails.client_name}</h5>
                  <h5>Client Id: {clientId}</h5>
                </div>
              )}
              {reportType === "environment" && (
                <div>
                  <h5>Client Name: {queryParams.clientName}</h5>
                  <h5>Environment Name: {queryParams.environmentName}</h5>
                  <h5>Environment Id: {clientEnvironmentId}</h5>
                </div>
              )}
              {reportType === "channel" && (
                <div>
                  <h5>Client Name: {queryParams.clientName}</h5>
                  <h5>Environment Name: {queryParams.environmentName}</h5>
                  <h5>Channel Name: {queryParams.channelName}</h5>
                </div>
              )}
              {reportType === "channel" && entitlementInfo && (
                <h5 className="card-category mb-2">
                  Storage Entitlement: {entitlementInfo.allotment_gb} GB
                  {entitlementInfo.usage_count > 0 && (
                    <span style={{ color: '#6c757d', fontWeight: 'normal' }}>
                      {' '}(Usage: {entitlementInfo.usage_count})
                    </span>
                  )}
                </h5>
              )}
            </div>
          </Col>
        </Row>
        {!showChannelDetail && (
          <Row>
            <Col md="12">
              <Card>
                <Card.Header>
                  <Card.Title>Message Storage Search</Card.Title>
                </Card.Header>
                <Card.Body>
                  {/* Date Range Selection */}
                  <Row className="align-items-end" id="message-storage-date-picker">
                    <Col md="4">
                      <Form.Group>
                        <label>Date *</label>
                        <CustomReactDatetime
                          inputProps={{
                            placeholder: "Select Date",
                            className: "form-control",
                          }}
                          value={selectedDate}
                          onChange={handleSelectedDateChange}
                          timeFormat={false}
                          dateFormat="YYYY-MM-DD"
                        />
                      </Form.Group>
                    </Col>
                    <Col md="4">
                      <Form.Group>
                        <label>Compare with Date</label>
                        <CustomReactDatetime
                          inputProps={{
                            placeholder: "Select Compare Date",
                            className: "form-control",
                            value: compareDate
                              ? moment(compareDate).format("YYYY-MM-DD")
                              : "",
                          }}
                          value={compareDate || ""}
                          onChange={handleCompareDateChange}
                          timeFormat={false}
                          dateFormat="YYYY-MM-DD"
                        />
                      </Form.Group>
                    </Col>
                    <Col md="4" className="mb-2">
                      <Button
                        id="message-storage-search-button"
                        variant="primary"
                        onClick={() => { fetchReportData() }}
                      >
                        Search
                      </Button>
                      <Button
                        id="message-storage-reset-button"
                        variant="default"
                        className="btn-outline ml-4"
                        onClick={() => {
                          setSelectedDate(moment());
                          setCompareDate(null);
                          setShowDownloadGridResults(false);
                        }}
                      >
                        Reset
                      </Button>
                    </Col>
                  </Row>

                  {/* Summary Cards */}
                  <div id="message-storage-summary">
                  {data.length > 0 && (
                    <Row className="mt-4">
                      <Col md="12">
                        <Card.Title>Message Storage Details</Card.Title>
                        <div className="numbers">
                          {(queryParams.allotmentGb || entitlementInfo?.allotment_gb) && (
                            <p className="card-category mb-2">
                              Storage Entitlement: {queryParams.allotmentGb || entitlementInfo?.allotment_gb || 0} GB
                              {entitlementInfo?.usage_count > 0 && (
                                <span style={{ color: '#6c757d', fontWeight: 'normal' }}>
                                  {' '}(Usage: {entitlementInfo.usage_count})
                                </span>
                              )}
                            </p>
                          )}
                          <p className="card-category mb-2">
                            Total Storage Used ({selectedDate ? selectedDate.format('YYYY-MM-DD') : 'Date'}): {totalStorageGb} GB
                          </p>
                          {compareDate !== null && columns.length > 2 && (
                            <p className="card-category">
                              Total Storage Used ({compareDate.format('YYYY-MM-DD')}): {totalPreviousStorageGb} GB
                            </p>
                          )}
                        </div>
                      </Col>
                    </Row>
                  )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}


        {/* Data Table Row */}
        <Row>
          <Col md="12">
            <div id="message-storage-data-grid">
            {/* Channel Detail View */}
            {showChannelDetail && (reportType === "channel" || selectedChannel) && (
              <div>
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <h5 className="mt-3 text-muted">Loading channel details...</h5>
                  </div>
                ) : channelDetailData.length > 0 ? (
                  <ReactTable
                    data={channelDetailData}
                    totalCount={channelDetailTotalCount}
                    functionCallBack={fetchChannelData}
                    setStateForPagnination={setStateForPagnination}
                    currentLimit={currentLimit}
                    currentOffset={currentOffset}
                    ascOrDescValue={ascOrDescValue}
                    sortValue={sortValue}
                    numberOfRowsData={numberOfRowsData}
                    tableName={`Reported Days`}
                    CSVHeaders={getDynamicCSVHeaders()}
                    CSVFileName={`channel_${selectedChannel?.channel_name || channelId}_storage_details`}
                    columns={getChannelDetailColumns()}
                    columnMapping={getColumnMapping()}
                    notify={notify}
                    showDownloadGridResults={showDownloadGridResults}
                    makePaginationStatic={true}
                  />
                ) : (
                  <div className="text-center py-5">
                    <i className="nc-icon nc-paper-2" style={{ fontSize: "48px", color: "#ccc" }}></i>
                    <h5 className="mt-3 text-muted">No Storage Data</h5>
                    <p className="text-muted">
                      No storage data available for this channel.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Main Table View - Show appropriate table based on report type */}
            {!showChannelDetail && columns.length > 0 && data.length > 0 && (
              <ReactTable
                data={data}
                totalCount={totalCount}
                functionCallBack={fetchReportData}
                setStateForPagnination={setStateForPagnination}
                currentLimit={currentLimit}
                currentOffset={currentOffset}
                ascOrDescValue={ascOrDescValue}
                sortValue={sortValue}
                numberOfRowsData={numberOfRowsData}
                tableName={`${reportType == 'client' ? `Environments` : `Channels`}`}
                CSVHeaders={getDynamicCSVHeaders()}
                CSVFileName={`${reportType}_message_storage_${compareDate ? compareDate.format('YYYY-MM-DD') : 'no_compare'}_to_${selectedDate.format('YYYY-MM-DD')}`}
                columns={columns}
                columnMapping={getColumnMapping()}
                notify={notify}
                showDownloadGridResults={showDownloadGridResults}
                makePaginationStatic={true}  // Static pagination only for client view
              />
            )}

            {/* Empty State */}
            {!showChannelDetail && !loading && data.length === 0 && columns.length > 0 && (
              <Card>
                <Card.Body>
                  <div className="text-center py-5">
                    <i className="nc-icon nc-paper-2" style={{ fontSize: "48px", color: "#ccc" }}></i>
                    <h5 className="mt-3 text-muted">There's no data to display for your current selection.</h5>
                    <p className="text-muted">
                      Try expanding your date range or enabling archiving on at least one channel.
                    </p>
                  </div>
                </Card.Body>
              </Card>
            )}
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default MessageStorage;
